// Reading preferences (typography and theme), persisted per browser.
//
// Yera only ever shipped a font-family dropdown; everything else in its config
// panel was commented out. These are stored locally rather than server-side on
// purpose: they describe *this screen* — a phone and a desktop want different
// measures — so syncing them would make both worse.

export type FontKey = 'serif' | 'sans' | 'mono';
export type ThemeKey = 'auto' | 'light' | 'dark' | 'sepia';

export interface ReaderPrefs {
  font: FontKey;
  fontSize: number;
  lineHeight: number;
  measure: number;
  theme: ThemeKey;
}

interface Limit { min: number; max: number; step: number }

export const FONT_STACKS: Record<FontKey, string> = {
  serif: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono: '"SF Mono", "Cascadia Code", Consolas, monospace',
};

export const DEFAULTS: ReaderPrefs = {
  font: 'serif',
  fontSize: 19,     // px
  lineHeight: 1.6,
  measure: 68,      // characters per line
  theme: 'auto',    // 'auto' | 'light' | 'dark' | 'sepia'
};

export const LIMITS: Record<'fontSize' | 'lineHeight' | 'measure', Limit> = {
  fontSize: { min: 14, max: 28, step: 1 },
  lineHeight: { min: 1.2, max: 2.2, step: 0.1 },
  measure: { min: 45, max: 100, step: 5 },
};

const STORAGE_KEY = 'nexus_reader_prefs';

function clamp(value: unknown, { min, max }: Limit, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function loadPrefs(storage: Storage | undefined = globalThis.localStorage): ReaderPrefs {
  let stored: Partial<ReaderPrefs> = {};
  try {
    stored = JSON.parse(storage?.getItem(STORAGE_KEY) || '{}');
  } catch {
    // Corrupt or unavailable storage falls back to defaults rather than
    // breaking the reader.
  }
  return {
    font: stored.font && FONT_STACKS[stored.font] ? stored.font : DEFAULTS.font,
    fontSize: clamp(stored.fontSize, LIMITS.fontSize, DEFAULTS.fontSize),
    lineHeight: clamp(stored.lineHeight, LIMITS.lineHeight, DEFAULTS.lineHeight),
    measure: clamp(stored.measure, LIMITS.measure, DEFAULTS.measure),
    theme: stored.theme && ['auto', 'light', 'dark', 'sepia'].includes(stored.theme)
      ? stored.theme
      : DEFAULTS.theme,
  };
}

export function savePrefs(prefs: ReaderPrefs, storage: Storage | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Private browsing can refuse writes; the prefs still apply this session.
  }
}

/** Push prefs onto an element as CSS custom properties. */
export function applyPrefs(prefs: ReaderPrefs, root: HTMLElement = document.documentElement): void {
  root.style.setProperty('--reader-font', FONT_STACKS[prefs.font]);
  root.style.setProperty('--reader-size', `${prefs.fontSize}px`);
  root.style.setProperty('--reader-leading', String(prefs.lineHeight));
  root.style.setProperty('--reader-measure', `${prefs.measure}ch`);
  root.dataset.theme = prefs.theme;
}
